/**
 * Demography API Service
 * Migrated from 3in1cms legacy/demogApi.ts
 *
 * Full implementation with masking, filtering, and category processing
 */

import { request } from "../core/request";
import { API_RESPONSE_CODES, OPERATION_IDS } from "./constants";
import type {
  ICustomerRelationshipResponse,
  ICustRelationshipDetails,
  ArrayProductKey,
  NonArrayProductKey,
  MaskConfig,
  IRelationsApiResponse,
  ProductNames,
  Loan,
  Subscription,
} from "../types/relationshipTypes";
import { createCacheAsync } from "@repo/shared-utils/cache";

// Response type for getRelationshipDetails
export interface IRelationshipDetailsResponse {
  data: IRelationsApiResponse;
  statusCode: string;
  message: string;
  status: string;
}

// Constants for masking sensitive fields
const FIELDS_TO_BE_MASKED: Record<string, MaskConfig[]> = {
  loan: [
    { fields: "agreementNo", start: 0, end: 4 },
    { fields: "relAmount", start: 0 },
  ],
  Cards: [
    { fields: "cardNumber", start: 0, end: 4 },
    { fields: "availableLimit", start: 0 },
    { fields: "accCreditLimit", start: 0 },
  ],
  insurance: [
    { fields: "agreementNo", start: 0, end: 4 },
    { fields: "policyNo", start: 0, end: 4 },
    { fields: "premiumAmount", start: 0 },
  ],
  deposits: [
    { fields: "depositId", start: 0, end: 4 },
    { fields: "relAmount", start: 0 },
  ],
} as const;

// Product category mappings
const CUST_RELATION_PRODUCTS_CATEGORY: Record<string, string> = {
  PL: "PERSONAL LOAN",
  PBL: "PROFESSIONAL & BUSINESS LOAN",
  LAGJ: "LOAN AGAINST GOLD JEWELLERY",
  DEPOSITS: "DEPOSITS",
  LAS: "LOAN AGAINST SECURITIES",
  AL: "AUTOMOBILE LOAN",
  IVL: "INSURANCE|VAS LOAN",
  CDL: "CONSUMER DURABLE LOAN",
  MFL: "MICROFINANCE LOAN",
  BL: "BUSINESS LOAN",
  FD: "FIXED DEPOSIT",
  SDPM: "SYSTEMATIC DEPOSIT PLAN- MMS",
  SDPS: "SYSTEMATIC DEPOSIT PLAN- SMS",
  EMI: "EMI CARD",
  HEALTH: "HEALTH CARD",
  HINS: "HEALTH INSURANCE",
  VAS: "VALUE ADDED SERVICES",
  GINS: "GENERAL INSURANCE",
  LINS: "LIFE INSURANCE",
  MINS: "MOTOR INSURANCE",
  BAJAJPRIME: "BAJAJ PRIME",
  CREDITPASS: "CREDIT PASS",
  LAP: "LOAN AGAINST PROPERTY",
  HOUSINGLOAN: "HOUSING LOAN",
  PEMFR: "PROF EQUIPMENT FINANCING",
  LAIP: "LOAN AGAINST INSURANCE POLICY",
} as const;

const NON_ARRAY_PRODUCTS = ["mutualFunds", "creditCards", "isBHFLLoanFlag"] as const;
const ARRAY_PRODUCTS = ["Cards", "insurance", "deposits", "loan", "subscription"] as const;

/**
 * Mask sensitive data with asterisks
 */
const maskData = (text: string, start: number, end = 0, maskSymbol = "*"): string => {
  try {
    const total = text.length;
    const maskedLength = Math.max(0, total - (start + end));

    if (maskedLength === 0) return text;

    const maskSymbols = maskSymbol.repeat(maskedLength);
    const regex = new RegExp(`(.{${start}}).{${maskedLength}}(.{${end}})`);
    return text.replace(regex, `$1${maskSymbols}$2`);
  } catch {
    return text;
  }
};

/**
 * Apply masking to specific fields in an object
 */
const maskKeys = (item: Record<string, unknown>, fieldsToBeMasked: MaskConfig[]): void => {
  for (const { fields, start = 0, end = 0 } of fieldsToBeMasked) {
    const value = item[fields];
    if (typeof value === "string") {
      item[fields] = maskData(value, start, end);
    }
  }
};

/**
 * Add expiry flag to subscriptions
 */
const addExpiryFlag = (item: Subscription): Subscription & { isExpired: number } => ({
  ...item,
  isExpired: new Date(item.expiryDate) < new Date() ? 1 : 0,
});

/**
 * Filter products by category
 */
const filterByCategory = <T extends Loan | Subscription>(
  items: T[],
  product: ArrayProductKey,
  categoryFilter?: string
): T[] => {
  if (!categoryFilter) return items;

  return items.filter((item) => {
    const itemCategory =
      product === "subscription"
        ? (item as Subscription).prodDesc?.toUpperCase()
        : (item as Loan).prodCategory?.toUpperCase();
    return itemCategory?.includes(categoryFilter.toUpperCase());
  });
};

/**
 * Process array-type products (loans, cards, insurance, etc.)
 */
const processArrayProduct = <T extends Loan[] | Subscription[] | null>(
  productData: T,
  product: ArrayProductKey,
  categoryFilter?: string,
  shouldMask = false
): T extends null ? never[] : T => {
  if (!Array.isArray(productData)) return [] as T extends null ? never[] : T;

  let processedData = [...productData];

  // Add expiry flag for subscriptions
  if (product === "subscription") {
    processedData = (processedData as Subscription[]).map((item) => addExpiryFlag(item));
  }

  // Apply category filtering
  processedData = filterByCategory(
    processedData as (Loan | Subscription)[],
    product,
    categoryFilter
  );

  // Apply masking if required
  if (shouldMask && FIELDS_TO_BE_MASKED[product]) {
    for (const item of processedData) {
      maskKeys(item as Record<string, unknown>, FIELDS_TO_BE_MASKED[product]);
    }
  }

  return processedData as T extends null ? never[] : T;
};

/**
 * Process all products when no specific product is requested
 */
const processAllProducts = (
  custRelationshipDetails: ICustRelationshipDetails,
  categoryFilter: string | undefined,
  isMaskingRequired: boolean,
  result: Record<string, unknown>
): Record<string, unknown> => {
  // Process array products
  for (const product of ARRAY_PRODUCTS) {
    const productData = custRelationshipDetails[product];
    result[product] = processArrayProduct(
      productData as Loan[] | Subscription[],
      product,
      categoryFilter,
      isMaskingRequired
    );
  }

  // Add non-array products
  for (const product of NON_ARRAY_PRODUCTS) {
    result[product] = custRelationshipDetails[product];
  }

  return result;
};

/**
 * Process a specific product when requested
 */
const processSpecificProduct = (
  custRelationshipDetails: ICustRelationshipDetails,
  productName: string,
  categoryFilter: string | undefined,
  isMaskingRequired: boolean,
  result: Record<string, unknown>
): Record<string, unknown> => {
  if (productName in custRelationshipDetails) {
    if (NON_ARRAY_PRODUCTS.includes(productName as NonArrayProductKey)) {
      result[productName] = custRelationshipDetails[productName as keyof ICustRelationshipDetails];
    } else if (ARRAY_PRODUCTS.includes(productName as ArrayProductKey)) {
      const productData = custRelationshipDetails[productName as keyof ICustRelationshipDetails];
      result[productName] = processArrayProduct(
        productData as Loan[] | Subscription[],
        productName as ArrayProductKey,
        categoryFilter,
        isMaskingRequired
      );
    }
  } else if (ARRAY_PRODUCTS.includes(productName as ArrayProductKey)) {
    // Set default values when product doesn't exist
    result[productName] = [];
  } else if (NON_ARRAY_PRODUCTS.includes(productName as NonArrayProductKey)) {
    result[productName] = null;
  }

  return result;
};

/**
 * Process the relationship response with filtering, masking, and categorization
 */
const processRelationshipResponse = (
  custRelationshipDetails: ICustRelationshipDetails,
  productName?: string,
  productCategory?: string,
  isMaskingRequired = false
): Record<string, unknown> => {
  const normalizedProductName = productName === "all" ? undefined : productName;
  const normalizedCategory = productCategory === "all" ? undefined : productCategory;
  const categoryFilter = normalizedCategory
    ? CUST_RELATION_PRODUCTS_CATEGORY[normalizedCategory] || normalizedCategory
    : undefined;

  const result: Record<string, unknown> = {
    customerId: custRelationshipDetails.customerId,
  };

  // If no specific product requested, process all products
  if (!normalizedProductName) {
    return processAllProducts(custRelationshipDetails, categoryFilter, isMaskingRequired, result);
  }

  // Process specific product
  return processSpecificProduct(
    custRelationshipDetails,
    normalizedProductName,
    categoryFilter,
    isMaskingRequired,
    result
  );
};

/**
 * Get user details / demography data (simplified version)
 * @param mobileNo - Mobile number
 * @returns User relations and details
 */
export const getUserDetails = async (mobileNo: string): Promise<ICustomerRelationshipResponse> => {
  try {
    const response = await request<ICustomerRelationshipResponse, { mobileNo: string }>({
      operationId: OPERATION_IDS.custRelationship,
      requestData: {
        mobileNo,
      },
    });

    return response;
  } catch (error) {
    console.error("Error in getUserDetails", error);
    throw error;
  }
};

/**
 * Get relationship details with full filtering, masking, and processing
 * This is the complete version with all features from 3in1cms
 *
 * @param mobileNo - User's mobile number
 * @param productName - Optional product filter (e.g., 'loan', 'Cards', 'insurance')
 * @param productCategory - Optional category filter (e.g., 'PL', 'LAGJ', 'HEALTH')
 * @param isMaskingRequired - Whether to mask sensitive fields
 * @returns Processed relationship data
 */
export const getRelationshipDetails = async (
  mobileNo: string,
  productName?: ProductNames,
  productCategory?: string,
  isMaskingRequired?: boolean
): Promise<IRelationshipDetailsResponse> => {
  try {
    if (!mobileNo) {
      throw new Error("Mobile number is required but was not provided.");
    }

    const response = await createCacheAsync(
      `${OPERATION_IDS.customerRelations}-${mobileNo}`,
      request<ICustomerRelationshipResponse, { mobileNumber: string }>,
      {
        operationId: OPERATION_IDS.customerRelations,
        requestData: {
          mobileNumber: mobileNo,
        },
      }
    );

    if (response.statusCode !== API_RESPONSE_CODES.STATUS_CODE_7001) {
      throw new Error(JSON.stringify(response));
    }
    const custDetails =
      response.custRelationshipDetails || response.custRelationshipDetailsList?.[0];

    if (!custDetails) {
      throw new Error("Customer relationship details not found in response.");
    }

    const processedResponse = processRelationshipResponse(
      custDetails,
      productName,
      productCategory
        ? CUST_RELATION_PRODUCTS_CATEGORY[productCategory] || productCategory
        : undefined,
      isMaskingRequired
    );

    return {
      data: {
        ...processedResponse,
        customerId: custDetails.customerId,
      } as IRelationsApiResponse,
      statusCode: "90",
      message: "Relation Details fetched",
      status: "success",
    };
  } catch (error) {
    console.error("Error in getRelationshipDetails", error);
    throw error;
  }
};
